import React, { useState } from "react";
import moment from "moment/moment";

import DownloadFolderIcon from "icons/folder-download.svg";
import DownloadFileIcon from "icons/file-download.svg";

import { NotificationManagerAPI } from "notifications/NotificationManager";
import { downloadReports } from "reports/ReportsDownloader";
import { useAPI } from "services/Api";

import * as R from "reports/Report"

interface ExternalReportsRowProps {
    notificationManager: NotificationManagerAPI;
    parentComponentName: string;
    patient: any;
    patientOutcomeReports: any[] | null;
}

const ExternalReportsRow = ({
    notificationManager,
    parentComponentName,
    patient,
    patientOutcomeReports = []
}: ExternalReportsRowProps) => {
    const componentName = `${parentComponentName || ""}[ExternalOutcomeReportsRow]`;

    const API = useAPI();
    const [isOpen, setIsOpen] = useState(false);

    // ---=== Utilities ===--- //

    const handleToggleOpen = (event) => {
        event.preventDefault();
        setIsOpen(!isOpen);
    };

    const downloadReportPDF = (report) => {
        if (report.id) {
            void downloadReports(
                componentName,
                null,
                report.id,
                notificationManager,
                API
            );
        }
    };

    const downloadAllReportPDFs = () => {
        if (patient.id) {
            void downloadReports(
                componentName,
                patient.id,
                null,
                notificationManager,
                API
            );
        }
    };

    // ---=== Render ===--- //

    const renderReportRows = () => {
        let reportRows = [];

        patientOutcomeReports.forEach(
            (report, _index) => {
                let reportWeek = report.report_week || report.sample_plan_weeks;
                let reportApproved = report.status === "Approved";

                reportRows.push(
                    <React.Fragment key={`reports-${patient.id}-${report.id}`}>
                        <tr className="relative whitespace-nowrap h-[58px] text-xs bg-brand-50 border-x-0 border-y-2 border-gray-200">
                            <td/>
                            <td colSpan={3} className="absolute py-2 leading-[36px]">
                                <button
                                    className={`w-full text-center ${reportApproved ? "bg-brand-600 hover:bg-brand-500" : "bg-gray-400"} text-white rounded-lg cursor-pointer`}
                                    onClick={
                                        reportApproved
                                            ? (_event) => {void downloadReportPDF(report);}
                                            : undefined
                                    }
                                >
                                    <div className="flex items-center rounded-lg">
                                        <div className="w-[137px] flex justify-center items-center rounded-l-lg">
                                            {!R.isMonth6Report(reportWeek) ? `Week ${reportWeek}` : "6 Months Post"}
                                        </div>
                                        <div className="w-[152px] flex justify-center items-center">
                                            {reportApproved ? moment(report.prepared_date).format("MMM D, YYYY") : "-"}
                                        </div>
                                        <div className="w-[170px] flex justify-start items-center rounded-r-lg">
                                            {
                                                <div className="inline-flex justify-center">
                                                    {
                                                        reportApproved
                                                            ? (
                                                                <div className="flex items-center gap-1 ml-10">
                                                                    <DownloadFileIcon/>
                                                                    <div>Download</div>
                                                                </div>
                                                            )
                                                            : (
                                                                <div className="ml-14">
                                                                    In Progress
                                                                </div>
                                                            )
                                                    }
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </button>
                            </td>
                            <td/>
                            <td/>
                            <td/>
                            <td/>
                            <td/>
                        </tr>
                    </React.Fragment>
                );
            });

        return reportRows;
    };

    return (
        <React.Fragment key={`reports-${patient.id}`}>
            <tr
                className={`whitespace-nowrap py-3 text-sm border-x-0 border-y-2 border-gray-200 ${patientOutcomeReports?.length > 0 ? "cursor-pointer" : ""}`}
                onClick={
                    patientOutcomeReports?.length > 0
                        ? handleToggleOpen
                        : undefined
                }
            >
                <td className="w-auto flex-inline px-6 text-left text-gray-900">
                    {
                        patient.profile.payer_type !== 2
                            ? `${patient.first_name} ${patient.last_name}`
                            : `#${patient.id}`
                    }
                </td>
                <td className="pr-4">
                    <div className={`flex justify-end items-center w-[120px] h-[30px] bg-brand-50 rounded-2xl`}>
                        {
                            <div className="text-brand-700">
                                {`${patientOutcomeReports ? patientOutcomeReports.length : 0} Reports`}
                            </div>
                        }
                        {
                            <div className="w-[18px] h-[18px] ml-2 mr-2.5">
                                {
                                    patientOutcomeReports?.length > 0 && (
                                        <img
                                            className="opacity-40"
                                            src={`/icons/chevron-${isOpen ? "down" : "right"}.svg`}
                                            alt="chevron"
                                        />
                                    )
                                }
                            </div>
                        }
                    </div>
                </td>
                <td className="w-[152px] h-[58px] px-2">
                    {
                        patientOutcomeReports?.length > 0 && (
                            <button
                                className="inline-flex whitespace-nowrap gap-2 justify-center items-center h-[32px] px-4 bg-brand-600 hover:bg-brand-500 text-white rounded-lg cursor-pointer"
                                onClick={
                                    patientOutcomeReports && patientOutcomeReports.length > 0
                                        ? (event) => {
                                            event.stopPropagation();
                                            void downloadAllReportPDFs();
                                        }
                                        : undefined
                                }
                            >
                                <DownloadFolderIcon/>
                                <div className="text-xs leading-tight">
                                    Download All
                                </div>
                            </button>
                        )
                    }
                </td>
                <td className="w-auto px-4 text-left text-gray-500">
                    {patient ? patient.profile.physician_name : "\u2014"}
                </td>
                <td className="w-auto px-4 text-left text-gray-500">
                    {patient ? patient.profile.third_party_administrators : "\u2014"}
                </td>
                <td className="w-auto px-4 text-left text-gray-500">
                    {patient ? patient.profile.insurers : "\u2014"}
                </td>
            </tr>
            {
                isOpen && patientOutcomeReports?.length > 0 && (
                    renderReportRows()
                )
            }
        </React.Fragment>
    );
};

export default ExternalReportsRow;
